import { Button } from "@package/subframe/components/Button";
import { Select } from "@package/subframe/components/Select";
import { TextField } from "@package/subframe/components/TextField";
import { FeatherSearch } from "@subframe/core";

import { AccountStatus } from "@/generated/model/accountStatus";
import { EmploymentStatus } from "@/generated/model/employmentStatus";
import { accountStatusLabel, employmentStatusLabel } from "@/lib/people";
import type {
  AcknowledgementFilter,
  DirectoryFilters,
  DirectoryOption,
} from "@/lib/people-directory";

const ALL = "all";

const ACKNOWLEDGEMENT_OPTIONS: {
  value: AcknowledgementFilter;
  label: string;
}[] = [
  { value: "HAS_OVERDUE", label: "Has overdue" },
  { value: "HAS_OUTSTANDING", label: "Has outstanding" },
  { value: "UP_TO_DATE", label: "Up to date" },
  { value: "NONE_SENT", label: "None sent" },
];

interface PeopleDirectoryFiltersProps {
  search: string;
  onSearchChange: (search: string) => void;
  filters: DirectoryFilters;
  onFiltersChange: (filters: DirectoryFilters) => void;
  rooms: DirectoryOption[];
  jobTitles: DirectoryOption[];
  showAccountFilter: boolean;
  canClear: boolean;
  onClear: () => void;
}

export function PeopleDirectoryFilters({
  search,
  onSearchChange,
  filters,
  onFiltersChange,
  rooms,
  jobTitles,
  showAccountFilter,
  canClear,
  onClear,
}: PeopleDirectoryFiltersProps) {
  function update<K extends keyof DirectoryFilters>(key: K, value: string) {
    onFiltersChange({
      ...filters,
      [key]: value === ALL || value === "" ? null : value,
    });
  }

  return (
    <div className="flex w-full flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center">
      <TextField
        className="h-auto w-full lg:w-72 lg:flex-none"
        variant="outline"
        size="base"
        icon={<FeatherSearch />}
      >
        <TextField.Input
          placeholder="Search name, email, username or ID"
          aria-label="Search employees"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
        />
      </TextField>
      <div className="flex grow flex-wrap items-center gap-2">
        <Select
          placeholder="All rooms"
          value={filters.roomId ?? ALL}
          onValueChange={(value) => update("roomId", value)}
        >
          <Select.Item value={ALL}>All rooms</Select.Item>
          {rooms.map((room) => (
            <Select.Item key={room.value} value={room.value}>
              {room.label}
            </Select.Item>
          ))}
        </Select>
        <Select
          placeholder="All roles"
          value={filters.jobTitle ?? ALL}
          onValueChange={(value) => update("jobTitle", value)}
        >
          <Select.Item value={ALL}>All roles</Select.Item>
          {jobTitles.map((title) => (
            <Select.Item key={title.value} value={title.value}>
              {title.label}
            </Select.Item>
          ))}
        </Select>
        <Select
          placeholder="All employment statuses"
          value={filters.employmentStatus ?? ALL}
          onValueChange={(value) => update("employmentStatus", value)}
        >
          <Select.Item value={ALL}>All employment statuses</Select.Item>
          {Object.values(EmploymentStatus).map((status) => (
            <Select.Item key={status} value={status}>
              {employmentStatusLabel(status)}
            </Select.Item>
          ))}
        </Select>
        {showAccountFilter && (
          <Select
            placeholder="All account statuses"
            value={filters.accountStatus ?? ALL}
            onValueChange={(value) => update("accountStatus", value)}
          >
            <Select.Item value={ALL}>All account statuses</Select.Item>
            {Object.values(AccountStatus).map((status) => (
              <Select.Item key={status} value={status}>
                {accountStatusLabel(status)}
              </Select.Item>
            ))}
          </Select>
        )}
        <Select
          placeholder="All acknowledgements"
          value={filters.acknowledgements ?? ALL}
          onValueChange={(value) => update("acknowledgements", value)}
        >
          <Select.Item value={ALL}>All acknowledgements</Select.Item>
          {ACKNOWLEDGEMENT_OPTIONS.map((option) => (
            <Select.Item key={option.value} value={option.value}>
              {option.label}
            </Select.Item>
          ))}
        </Select>
        <Button
          variant="neutral-tertiary"
          disabled={!canClear}
          onClick={onClear}
        >
          Clear
        </Button>
      </div>
    </div>
  );
}
